import { useAuth } from "@clerk/clerk-react";
import { Grid } from "@mui/material";
import { AlertContext } from "contexts/Alert";
import { OrganizationUserContext } from "contexts/Organization";
import { SplashPage } from "contexts/Subscription";
import { useContext, useEffect, useState } from "react";
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  useSearchParams,
} from "react-router-dom";
import { SearchParams } from "schemas/dashboard";
import { Plans } from "schemas/payments";
import { routes } from "schemas/routes";

import Loading from "components/Loading";
import { PAGE_VISITED } from "constants/trackingProps";
import { fetcherAuth } from "utils/api";
import { getSource, identifyUser, trackEvent } from "utils/tracking";
import { useSubscription } from "utils/useSubscription";

import { AuthAction } from "../schema";
import InstagramOnboardingPreference from "./Preference";
import InstagramOnboardingSignup from "./Signup";
import { InstagramOnboardingOptions } from "./schema";
import styles from "./styles";

const InstagramOnboardingFlow = () => {
  const { currentOrg, setCurrentUser, setCurrentOrg } = useContext(
    OrganizationUserContext,
  );
  const { setErrorAlert } = useContext(AlertContext);
  const navigate = useNavigate();
  const location = useLocation();
  const { getToken } = useAuth();
  const { handleCheckout } = useSubscription();
  const [searchParams] = useSearchParams();

  const [fetchUserLoading, setFetchUserLoading] = useState(true);
  const referralCode = searchParams.get(SearchParams.REFERRAL_CODE);
  const option = searchParams.get(SearchParams.OPTION);

  const source = getSource();

  const emailMatch = location.search?.match(/email=([^&]*)/);
  const email = emailMatch ? decodeURIComponent(emailMatch[1]) : "";

  const fetchUserInfo = async () => {
    setFetchUserLoading(true);
    try {
      const res = await fetcherAuth(getToken, `/api/organization/auth/user`);
      if (res) {
        identifyUser(res.organizationUser.id);
        setCurrentUser(res.organizationUser);
        setCurrentOrg(res.organization);
      }
      redirectToPage();
    } catch (error) {
      handleClerkUserSignUp();
    }
  };

  const handleClerkUserSignUp = async () => {
    try {
      const res = await fetcherAuth(
        getToken,
        `/api/organization/auth/signup`,
        "POST",
        {},
        {
          referralCode,
          source: referralCode ? "referral" : source,
          isInstagramOnboardingFlow: true,
          option,
        },
      );
      identifyUser(res.organizationUser.id);
      setCurrentUser(res.organizationUser);
      setCurrentOrg(res.organization);
      if (
        // @ts-ignore
        typeof window.fbq !== "undefined" &&
        // @ts-ignore
        typeof window.fbq === "function"
      ) {
        // @ts-ignore
        window.fbq("track", "CompleteRegistration");
      }
      if (
        // @ts-ignore
        typeof window.ttq !== "undefined" &&
        // @ts-ignore
        typeof window.ttq.track === "function"
      ) {
        // @ts-ignore
        window.ttq.track("CompleteRegistration");
      }
    } catch (error) {
      setErrorAlert(error);
      setFetchUserLoading(false);
    }
  };

  const redirectToPage = async () => {
    setFetchUserLoading(true);
    if (!option) {
      navigate(`/${routes.preferencesInstagramOnboarding}${location.search}`, {
        replace: true,
      });
      setFetchUserLoading(false);
      return;
    }

    if (currentOrg?.id) {
      if (option === InstagramOnboardingOptions.do_it_myself) {
        navigate(`/${routes.onboardingInfluencer}${location.search}`, {
          replace: true,
        });
      } else {
        await checkoutVaPlan();
      }
    }
    setFetchUserLoading(false);
  };

  const checkoutVaPlan = async () => {
    await handleCheckout(
      Plans.VA_YEARLY,
      window.location.pathname,
      source || "",
      SplashPage.instagram,
    );
  };

  useEffect(() => {
    if (currentOrg?.id) {
      redirectToPage();
    } else {
      fetchUserInfo();
      trackEvent(PAGE_VISITED);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentOrg?.id]);

  return fetchUserLoading ? (
    <Loading />
  ) : (
    <Grid container sx={styles.root}>
      <Grid sx={styles.formContainer} item xs={12}>
        <Routes>
          <Route
            index
            element={
              <InstagramOnboardingPreference checkoutVaPlan={checkoutVaPlan} />
            }
          />
          <Route
            path="/preferences"
            element={
              <InstagramOnboardingPreference checkoutVaPlan={checkoutVaPlan} />
            }
          />
          <Route
            path="/sign-up/*"
            element={
              <InstagramOnboardingSignup
                email={email}
                action={AuthAction.SIGN_UP}
              />
            }
          />
          <Route
            path="/sign-in/*"
            element={
              <InstagramOnboardingSignup
                email={email}
                action={AuthAction.SIGN_IN}
              />
            }
          />
        </Routes>
      </Grid>
    </Grid>
  );
};

export default InstagramOnboardingFlow;
